import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing, ensureUser } from '../../util/data';

const { Money } = sdkTypes;
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  Button,
  NamedLink,
  ResponsiveImage,
  ReviewRating,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

// Simple customer commission calculation for price display
const calculateSimpleCustomerCommission = (basePrice, customerCommissionPercentage) => {
  const commissionAmount = Math.round((basePrice.amount * customerCommissionPercentage) / 100);
  return new Money(basePrice.amount + commissionAmount, basePrice.currency);
};

// Utility function to calculate commission-adjusted price for customers
const calculateCustomerPriceWithCommission = (basePrice, config, commissionData) => {
  if (!basePrice) {
    return basePrice;
  }

  // Use commission data from Sharetribe Console if available, otherwise fallback to config
  let customerCommissionPercentage;

  if (commissionData?.customerCommission?.percentage) {
    customerCommissionPercentage = commissionData.customerCommission.percentage;
  } else if (config?.commission?.showCustomerCommission) {
    customerCommissionPercentage = config.commission.defaultCustomerCommissionPercentage;
  } else {
    // No commission configuration found, show base price
    return basePrice;
  }

  // Apply simple customer commission (just percentage of base price)
  return calculateSimpleCustomerCommission(basePrice, customerCommissionPercentage);
};

const priceData = (price, currency, intl, config = null, commissionData = null) => {
  if (price && price.currency === currency) {
    // Calculate commission-adjusted price for customers
    const adjustedPrice = calculateCustomerPriceWithCommission(price, config, commissionData);
    const formattedPrice = formatMoney(intl, adjustedPrice);

    // Log commission adjustments for transparency (only in development)
    if (process.env.NODE_ENV === 'development' && adjustedPrice.amount !== price.amount) {
      console.log('ListListingCard: Commission applied', {
        base: formatMoney(intl, price),
        total: formattedPrice,
      });
    }

    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, commissionData = null } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl,
    config,
    commissionData
  );
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    commissionData,
    commissionLoading,
    commissionError,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const brand = currentListing?.attributes?.publicData?.brand;
  const description = currentListing?.attributes?.description;
  const ratingData = [4, 3]; // Todo: Get data from source
  const rating = ratingData.length
    ? Math.floor(ratingData.reduce((a, b) => a + b, 0) / ratingData.length)
    : 0;

  return (
    <div className={classNames(classes, css.listListingCardContainer)}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.mainSectionInfo}>
        <div className={css.listCategoryTitle}>
          {richText(title.toLowerCase(), {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>
        {description && <div className={css.listDescription}>{description}</div>}
      </div>
      <div className={css.priceAndButtonsContainer}>
        <div className={css.priceContainer}>
          <div className={css.priceLabel}>Price</div>
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            commissionData={commissionData}
          />
        </div>
        <div className={css.listListingButtonContainer}>
          <NamedLink name="ListingPage" params={{ id, slug }}>
            <Button>Detail</Button>
          </NamedLink>
          <NamedLink name="ListingPage" params={{ id, slug }}>
            <Button>Buy Now</Button>
          </NamedLink>
        </div>
      </div>
      {/* <div className={css.info}>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} commissionData={commissionData} />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div> */}
    </div>
  );
};

ListListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListListingCardComponent);
